
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Hash, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SaasInstance } from '@/types/userRole';
import { Progress } from '@/components/ui/progress';
import { useSuperAdminReferralBank } from '@/hooks/useSuperAdminReferralBank';

interface InstanceReferralIdsTabProps {
  instanceId: string;
  instance: SaasInstance;
}

const InstanceReferralIdsTab: React.FC<InstanceReferralIdsTabProps> = ({ 
  instanceId,
  instance 
}) => {
  const navigate = useNavigate();
  const { bankStats } = useSuperAdminReferralBank();
  
  // Get the plan-based allocation limit
  const getPlanAllocationLimit = () => {
    switch (instance.plan) {
      case 'enterprise':
        return 5000;
      case 'premium':
        return 500;
      case 'basic':
        return 100;
      default:
        return 25;
    }
  };
  
  const planLimit = getPlanAllocationLimit();
  const allocatedIDs = bankStats?.instanceAllocations[instanceId] || 0;
  const percentAllocated = (allocatedIDs / planLimit) * 100;
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Hash className="mr-2 h-5 w-5 text-primary" />
          Referral IDs Management
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-sm text-gray-500 mb-2">
          Allocated IDs: {allocatedIDs} / {planLimit}
        </div>
        
        <Progress value={percentAllocated} className="h-2" />
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div className="p-4 bg-blue-50 rounded-lg">
            <div className="text-lg font-semibold">{allocatedIDs}</div>
            <div className="text-sm text-gray-500">Currently Allocated</div>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg">
            <div className="text-lg font-semibold">{planLimit}</div>
            <div className="text-sm text-gray-500">Plan Limit</div>
          </div>
        </div>
        
        <div className="mt-6">
          <Button
            onClick={() => navigate(`/super-admin/instance/${instanceId}/referral-ids`)}
            className="w-full flex items-center justify-center"
          >
            Manage Referral IDs
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
        
        <div className="text-sm text-gray-500 mt-4">
          <p>Manage the referral IDs allocated to this instance. You can allocate more IDs up to the plan limit.</p>
        </div>
      </CardContent>
    </Card>
  );
};

export default InstanceReferralIdsTab;
